import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Plus, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import PostCard from "../components/PostCard";
import CategoryBadge from "../components/CategoryBadge";
import EmptyState from "../components/EmptyState";
import { PostCardSkeleton } from "../components/Skeleton";
import { useAuthors } from "../hooks/useAuthors";
import { useReactionSummaries } from "../hooks/useReactionSummaries";

const EMPTY_ILLUSTRATION =
  "https://static.prod-images.emergentagent.com/jobs/1708729f-9648-4829-9f8b-880cdb94ab73/images/1956ccf3d83192578310ad2fc1bf54140a729526fe41e7729b9e7a245ecad4ec.png";

export default function Feed() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [activeFilter, setActiveFilter] = useState(null);
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);

  // Charger catégories une seule fois
  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  // Retour du callback OAuth LinkedIn : ?linkedin=connected|error
  // On affiche le message puis on nettoie l'URL (replaceState : pas de reload).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const li = params.get("linkedin");
    if (!li) return;
    if (li === "connected") {
      toast.success("Compte LinkedIn connecté.");
    } else {
      toast.error("La connexion LinkedIn a échoué. Réessayez depuis votre profil.");
    }
    params.delete("linkedin");
    const qs = params.toString();
    window.history.replaceState(
      null, "", window.location.pathname + (qs ? `?${qs}` : "")
    );
  }, []);

  const load = useCallback(
    async (opts) => {
      const { reset = false, currentFilter, currentPage } = opts;
      if (reset) setLoading(true);
      else setLoadingMore(true);
      setError(null);
      try {
        const params = new URLSearchParams({ page: currentPage, page_size: 20 });
        if (currentFilter) params.append("category_id", currentFilter);
        const { data } = await api.get(`/posts?${params}`);
        const newPosts = data.posts || [];
        setPosts((prev) => (reset ? newPosts : [...prev, ...newPosts]));
        setHasMore(newPosts.length === 20);
      } catch (e) {
        setError("Impossible de charger le feed.");
        toast.error("Impossible de charger le feed.");
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    // Stable : `api`/`toast` sont des imports, les setters useState sont stables,
    // les valeurs dynamiques (filter, page, reset) sont passées via `opts`.
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Recharger quand filtre change
  useEffect(() => {
    setPage(1);
    load({ reset: true, currentFilter: activeFilter, currentPage: 1 });
  }, [activeFilter, load]);

  // Résolution batch des auteurs
  const authorKeys = useMemo(() => posts.map((p) => p.author_id), [posts]);
  const authors = useAuthors(authorKeys);

  // Détail des réactions par type (un seul appel groupé pour tout le fil)
  const postIds = useMemo(() => posts.map((p) => p.post_id), [posts]);
  const reactionSummaries = useReactionSummaries(postIds);

  const catById = useMemo(
    () => Object.fromEntries(categories.map((c) => [c.category_id, c])),
    [categories]
  );

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    load({ reset: false, currentFilter: activeFilter, currentPage: next });
  };

  return (
    <div data-testid="feed-page">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1
            className="text-[28px] sm:text-[32px] leading-tight tracking-tight font-bold"
            style={{ fontFamily: "'Sora', sans-serif", color: "#1A1A1A" }}
          >
            Le feed
          </h1>
          <p className="text-sm mt-1" style={{ color: "#666" }}>
            L'actualité du réseau APOLLO, à hauteur de votre périmètre.
          </p>
        </div>
        <button
          onClick={() => navigate("/compose")}
          data-testid="feed-compose-button"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-white text-sm transition-all hover:opacity-90 active:scale-95"
          style={{ background: "#F77F00", boxShadow: "0 6px 18px rgba(247,127,0,0.25)" }}
        >
          <Plus size={16} /> Publier
        </button>
      </div>

      {/* Filters chips */}
      <div
        className="flex gap-2 overflow-x-auto pb-3 mb-6 -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-thin"
        data-testid="feed-filters"
        style={{ scrollbarWidth: "thin" }}
      >
        <button
          onClick={() => setActiveFilter(null)}
          data-testid="filter-chip-all"
          className="flex-shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition-all border whitespace-nowrap"
          style={{
            background: activeFilter === null ? "#1A1A1A" : "#FFF",
            borderColor: activeFilter === null ? "#1A1A1A" : "#E5E5E5",
            color: activeFilter === null ? "#FFF" : "#1A1A1A",
          }}
        >
          Tous
        </button>
        {categories.map((cat) => (
          <button
            key={cat.category_id}
            onClick={() =>
              setActiveFilter(activeFilter === cat.category_id ? null : cat.category_id)
            }
            data-testid={`filter-chip-${cat.code}`}
            className="flex-shrink-0 px-2 py-1 rounded-full transition-all border whitespace-nowrap"
            style={{
              background: activeFilter === cat.category_id ? `${cat.color}15` : "#FFF",
              borderColor: activeFilter === cat.category_id ? cat.color : "#E5E5E5",
            }}
          >
            <CategoryBadge category={cat} size="sm" />
          </button>
        ))}
      </div>

      {/* Loading initial */}
      {loading && (
        <div data-testid="feed-loading">
          {[0, 1, 2].map((i) => (
            <PostCardSkeleton key={i} />
          ))}
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div
          className="text-center py-8 bg-white rounded-[12px]"
          style={{ border: "1px solid #FEE2E2" }}
          data-testid="feed-error"
        >
          <p style={{ color: "#EF4444" }}>{error}</p>
          <button
            onClick={() => load({ reset: true, currentFilter: activeFilter, currentPage: 1 })}
            className="mt-3 text-sm font-medium underline"
            style={{ color: "#F77F00" }}
          >
            Réessayer
          </button>
        </div>
      )}

      {/* Empty */}
      {!loading && !error && posts.length === 0 && (
        <EmptyState
          illustration={EMPTY_ILLUSTRATION}
          title="Le feed est encore vide"
          description="Soyez le premier à partager une actualité, un succès ou un événement à votre réseau."
          testid="feed-empty"
          cta={
            <button
              onClick={() => navigate("/compose")}
              data-testid="feed-empty-cta"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-white font-semibold text-sm transition-opacity hover:opacity-90"
              style={{ background: "#F77F00", boxShadow: "0 6px 18px rgba(247,127,0,0.25)" }}
            >
              <Sparkles size={14} /> Publier maintenant
            </button>
          }
        />
      )}

      {/* Posts list */}
      <AnimatePresence mode="popLayout">
        {!loading &&
          !error &&
          posts.map((p) => (
            <PostCard
              key={p.post_id}
              post={p}
              category={catById[p.category_id]}
              author={authors[p.author_id]}
              reactionSummary={reactionSummaries[p.post_id]}
              onDeleted={(pid) =>
                setPosts((prev) => prev.filter((x) => x.post_id !== pid))
              }
            />
          ))}
      </AnimatePresence>

      {/* Load more */}
      {!loading && hasMore && posts.length > 0 && (
        <button
          onClick={loadMore}
          disabled={loadingMore}
          className="w-full py-3 rounded-lg border text-sm font-medium transition-colors hover:bg-gray-50 disabled:opacity-50"
          style={{ borderColor: "#E5E5E5", color: "#666" }}
          data-testid="feed-load-more"
        >
          {loadingMore ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Chargement…
            </span>
          ) : (
            "Charger plus"
          )}
        </button>
      )}
    </div>
  );
}
